/** GL category document types that belong on the GL Sync upload list (not GL Account). */
export const GL_SYNC_UPLOAD_DOCUMENT_TYPES = [
  'Trial Balance Transactions',
  'Investor Transactions',
  'Investment Transactions',
  'Entity Data',
  'Yardi Trial Balance',
  'Yardi Batch',
  'Allvue Account Group',
  'Allvue Entity Mapping',
];

/** Yardi → Allvue POC types (tenant-onboarded; T_124 / DEV_T_102). */
export const YARDI_ALLVUE_POC_DOCUMENT_TYPES = [
  'Yardi Trial Balance',
  'Yardi Batch',
  'Allvue Account Group',
  'Allvue Entity Mapping',
];

export const GL_SYNC_TRIAL_BALANCE_DOC_TYPE = 'Trial Balance Transactions';

export const GL_SYNC_S3_CATEGORY_BY_DOC_TYPE = {
  'Trial Balance Transactions': 'GL_SYNC_TRIAL_BALANCE',
  'GL Sync': 'GL_SYNC_TRIAL_BALANCE',
  'Investor Transactions': 'GL_SYNC_INVESTOR',
  'Investment Transactions': 'GL_SYNC_INVESTMENT',
  'Entity Data': 'GL_SYNC_ENTITY',
  'Yardi Trial Balance': 'GL_SYNC_TRIAL_BALANCE',
  'Yardi Batch': 'GL_SYNC_TRIAL_BALANCE',
  'Allvue Account Group': 'GL_SYNC_ENTITY',
  'Allvue Entity Mapping': 'GL_SYNC_ENTITY',
};

/** Exact Snowflake file-type folders (not derived from source system name). */
export const GL_SYNC_FILE_TYPE_FOLDER_BY_DOC_TYPE = {
  'Yardi Trial Balance': 'YARDI_TRIALBALANCE',
  'Yardi Batch': 'YARDI_BATCH',
  'Allvue Account Group': 'ALLVUE_ACCOUNT_GROUPS',
  'Allvue Entity Mapping': 'ALLVUE_MAPPING_CODES',
};

export const ALLVUE_NON_ALLOCATED_MEMO_VIEW =
  'WORKFLOW.GL_SYNC_OUTPUT_ALLVUE_NON_ALLOCATED_GL_ENTRIES_VIEW';

/** Folder Snowflake reads one directory back from the file (file type). */
export const buildGlSyncFileTypeFolder = (documentTypeName, sourceSystemName) => {
  const doc = String(documentTypeName || '').trim();
  const explicit = GL_SYNC_FILE_TYPE_FOLDER_BY_DOC_TYPE[doc];
  if (explicit) return explicit;
  const system = String(sourceSystemName || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');
  if (doc === 'Entity Data') return 'FUND_LIST';
  if (!system) return '';
  if (doc === 'Trial Balance Transactions' || doc === 'GL Sync') return `${system}_TRIAL_BALANCE`;
  if (doc === 'Investor Transactions') return `${system}_INVESTOR`;
  if (doc === 'Investment Transactions') return `${system}_INVESTMENT`;
  return `${system}_TRANSACTIONS`;
};

/** Display/API labels: keep system name only (S3 folder may be ALLVUE_TRIAL_BALANCE). */
export const stripGlSystemLabel = (value) => {
  if (value == null || value === '' || value === '-') return '';
  return String(value)
    .replace(
      /(_TRIAL_BALANCE|_TRIALBALANCE|_TRANSACTIONS|_INVESTMENT|_INVESTOR|_BATCH|_ACCOUNT_GROUPS|_MAPPING_CODES|_GL)$/i,
      ''
    )
    .trim();
};

/**
 * Map UI / card labels (Tally, TALLY, TALLY_GL) to chart_of_account.gl_system
 * as stored for GL Sync (e.g. TALLY_GL).
 */
export const toChartOfAccountGlSystem = (systemName) => {
  if (systemName == null || systemName === '') return '';
  let normalized = String(systemName)
    .trim()
    .replace(/\s+/g, '_')
    .toUpperCase();
  if (!normalized) return '';
  normalized = normalized.replace(
    /(_TRIAL_BALANCE|_TRIALBALANCE|_TRANSACTIONS|_INVESTMENT|_INVESTOR|_BATCH)$/i,
    ''
  );
  if (/_GL$/i.test(normalized)) return normalized;
  return `${normalized}_GL`;
};

/** Case/suffix-insensitive match for GL Sync system codes. */
export const glSystemsEqual = (a, b) => {
  const left = toChartOfAccountGlSystem(a);
  const right = toChartOfAccountGlSystem(b);
  return Boolean(left && right && left === right);
};

export const getDocumentTypeDisplayName = (item) =>
  String(item?.name || item?.documentTypeName || item?.documentType?.name || '').trim();

/**
 * Config category for conductor/config/default, keyed by document type.
 * Example: "Trial Balance Transactions" + COMPARISON → TRIAL_BALANCE_TRANSACTIONS_COMPARISON
 */
export const toGlSyncViewConfigCategory = (documentTypeName, kind) => {
  const raw = String(documentTypeName || '').trim();
  const doc = !raw || raw === 'GL Sync' ? GL_SYNC_TRIAL_BALANCE_DOC_TYPE : raw;
  const slug = doc
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  const suffix = String(kind || '')
    .trim()
    .toUpperCase()
    .replace(/^_+|_+$/g, '');
  return suffix ? `${slug}_${suffix}` : slug;
};

export const isYardiAllvuePocDocumentType = (name) =>
  YARDI_ALLVUE_POC_DOCUMENT_TYPES.includes(String(name || '').trim());

/** True for a Yardi ↔ Allvue mapping card (POC). Fund is optional; Tally cards stay on the old types. */
export const isYardiAllvueMappingPair = (sourceGL, destinationGL) => {
  const names = [sourceGL, destinationGL].map((value) =>
    stripGlSystemLabel(value).replace(/\s+/g, '').toUpperCase()
  );
  const hasYardi = names.some((n) => n.includes('YARDI'));
  const hasAllvue = names.some((n) => n.includes('ALLVUE'));
  return hasYardi && hasAllvue;
};

export const isGlSyncUploadDocumentType = (name) => {
  const n = String(name || '').trim();
  if (!n) return false;
  if (n === 'GL Sync') return true;
  return GL_SYNC_UPLOAD_DOCUMENT_TYPES.includes(n);
};

export const sortGlSyncDocumentTypes = (types) => {
  const list = Array.isArray(types) ? [...types] : [];
  return list.sort((a, b) => {
    const ai = GL_SYNC_UPLOAD_DOCUMENT_TYPES.indexOf(getDocumentTypeDisplayName(a));
    const bi = GL_SYNC_UPLOAD_DOCUMENT_TYPES.indexOf(getDocumentTypeDisplayName(b));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
};
